import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useMemo, useEffect} from 'react';
import {formatString, insertNewlines, parseColor, setAlpha} from '../utils/utils.js';
import {MenuButton} from '../components/menu-button.js';
import {isSoundTurnedOn, toggleSound, ToggleType} from '../utils/toggleSound.js';
import {
    Column,
    Container,
    Image,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {goToMenu} from '../utils/goToMenu.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Material, Texture} from '@wonderlandengine/api';
import {WindowHeader} from '../components/window-header.js';
import {openLink} from '../utils/openLink.js';

import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {Localization} from '../../utils/Localization.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {SliderItem} from '../components/SliderComponent.js';
import {Window} from '../components/window.js';
import {StatNames} from '../../enums/StatNames.js';
import {MalRoot} from '../components/mal-root.js';
import {GameOverModel} from '../../models/GameOverModel.js';
import {Header} from '../components/header.js';
import {BooleanName} from '../../enums/BooleanName.js';
import {Statistics} from '../../components/statistics.js';
import {BooleanStore} from '../../components/boolean-store.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {ColoredImage} from '../components/colored-image.js';
import {goToMainMenu} from '../utils/gotoMainMenu.js';
import {goToGame} from '../utils/goToGame.js';
import {GameFlowController} from '../../components/game-flow-controller.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {RankManager} from '../../components/rank-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {UnlockManager} from '../../components/unlock-manager.js';
import {RNG} from '../../utils/rng.js';

let bm: Material | null;

export function isGameModeLocked(booleanName: BooleanName) {
    const unlocked = BooleanStore.instance.get(booleanName);
    return !unlocked;
}

export function lockImage(booleanName: BooleanName, image: Texture): Texture {
    if (!isGameModeLocked(booleanName)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

type ModeSelectItem = {
    title: string;
    description: string;
    difficultyLevel: number;
    gamesPlayedStat: StatNames;
    autoPop: boolean;
    unlockedItem: BooleanName;
    lockFlag?: BooleanName;
    ignoreLock: boolean;
    implemented: boolean;
};

const GameModeData = new Map<GameModeType, ModeSelectItem>([
    [
        GameModeType.Quickplay,
        {
            title: 'Quickplay',
            description: 'MalModeSelectDescriptionQuickplay',
            difficultyLevel: 1,
            gamesPlayedStat: StatNames.QuickplayGamesPlayed,
            autoPop: false,
            unlockedItem: BooleanName.QuickplayModeClicked,
            ignoreLock: true,
            implemented: true,
        },
    ],
    [
        GameModeType.Cupcake,
        {
            title: 'Cupcake',
            description: 'MalModeSelectDescriptionCupcake',
            difficultyLevel: 1,
            gamesPlayedStat: StatNames.CupcakeGamesPlayed,
            autoPop: true,
            unlockedItem: BooleanName.CupcakeModeClicked,
            lockFlag: BooleanName.CupcakeModeUnlocked,
            ignoreLock: false,
            implemented: false,
        },
    ],
    [
        GameModeType.Easy,
        {
            title: 'Easy',
            description: 'MalModeSelectDescriptionEasy',
            ignoreLock: false,
            lockFlag: BooleanName.EasyModeUnlocked,
            unlockedItem: BooleanName.EasyModeClicked,
            gamesPlayedStat: StatNames.EasyGamesPlayed,
            autoPop: false,
            difficultyLevel: 2,
            implemented: true,
        },
    ],
    [
        GameModeType.Survival,
        {
            title: 'Skull',
            description: 'MalModeSelectDescriptionSkull',
            ignoreLock: false,
            lockFlag: BooleanName.SurvivalModeUnlocked,
            unlockedItem: BooleanName.SurvivalModeClicked,
            gamesPlayedStat: StatNames.SurvivalGamesPlayed,
            autoPop: false,
            difficultyLevel: 4,
            implemented: false,
        },
    ],
    [
        GameModeType.Normal,
        {
            title: 'Normal',
            description: 'MalModeSelectDescriptionNormal',
            ignoreLock: false,
            lockFlag: BooleanName.NormalModeUnlocked,
            unlockedItem: BooleanName.NormalModeClicked,
            gamesPlayedStat: StatNames.NormalGamesPlayed,
            autoPop: false,
            difficultyLevel: 5,
            implemented: false,
        },
    ],
    [
        GameModeType.Timed,
        {
            title: 'Timed',
            description: 'MalModeSelectDescriptionTimed',
            ignoreLock: false,
            lockFlag: BooleanName.TimedModeUnlocked,
            unlockedItem: BooleanName.TimedModeClicked,
            gamesPlayedStat: StatNames.TimedGamesPlayed,
            autoPop: true,
            difficultyLevel: 5,
            implemented: false,
        },
    ],
    [
        GameModeType.Fungus,
        {
            title: 'Fungus',
            description: 'MalModeSelectDescriptionFungus',
            ignoreLock: false,
            lockFlag: BooleanName.FungusModeUnlocked,
            unlockedItem: BooleanName.FungusModeClicked,
            gamesPlayedStat: StatNames.FungusGamesPlayed,
            autoPop: true,
            difficultyLevel: 3,
            implemented: false,
        },
    ],
]);

export const ModeSelectPanel = (props: React.PropsWithChildren<YogaNodeProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const material = rotbMats.titleBackgroundMaterial;
    // TK: This prevents the material to be cloned every time the component is rendered.
    // Probably needs to be useMemo or something, but that keeps getting reloaded anyway.
    if (!bm) {
        bm = material!.clone();
        (bm as any).setColor(parseColor(colorSwatch.TitleBackground));
    }

    const [selectedMode, setSelectedMode] = useState<GameModeType>(GameModeType.Quickplay);

    const [rank] = useState(
        formatString(
            Localization.instance.get('RankFormat'),
            formatNumber(RankManager.instance.rank)
        )
    );
    const [xpPercentage] = useState(RankManager.instance.currentRankPercentComplete);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [diffLevel, setDiffLevel] = useState(3);
    const [isImplemented, setIsImplemented] = useState(false);
    const [isLocked, setIsLocked] = useState(true);
    const [isAutoPop, setIsAutoPop] = useState(false);
    const [selectModeText] = useState(
        Localization.instance.get(
            RNG.getItem(['GameSetupMenuText1', 'GameSetupMenuText2', 'GameSetupMenuText3'])
        )
    );

    const onSelectionChanged = (item: SliderItem) => {
        const currentSelectedMode = GameModeType[item.id];
        const data = GameModeData.get(currentSelectedMode);
        setTitle(Localization.instance.get(data.title));
        const locked = data.ignoreLock ? false : isGameModeLocked(data.lockFlag!);
        if (!locked) {
            setDescription(Localization.instance.get(data.description));
        } else {
            const unlockInstruct = UnlockManager.instance.getInstructionsForUnlock(
                data.lockFlag,
                0
            );
            setDescription(unlockInstruct.instructions);
        }
        setDiffLevel(data.difficultyLevel);
        setSelectedMode(currentSelectedMode);
        setIsImplemented(data.implemented);
        setIsAutoPop(data.autoPop);
        setIsLocked(locked);
    };

    const handleClick = ({e}) => {
        GameFlowController.instance.setMode(selectedMode);
        goToMenu(MenuPanels.Loadout);
        //goToGame(e);
    };

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={Localization.instance.get('ModeSelect')} />
                        <Container marginLeft={-20} marginBottom={40}>
                            <MalRoot
                                rank={rank}
                                xpPercentage={xpPercentage}
                                additionalRotation={[-1.5, 1.5, 0]}
                                text={selectModeText}
                            ></MalRoot>
                        </Container>
                        <Container flexGrow={1}>
                            <Container>
                                <Carousel
                                    loop={false}
                                    onSelectionChanged={onSelectionChanged}
                                >
                                    <CarouselItem
                                        title="Star"
                                        id={'Quickplay'}
                                        image={UiTexturesManager.instance.gameModeStar}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Normal"
                                        id="Easy"
                                        image={UiTexturesManager.instance.gameModeNormal}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Cupcake"
                                        id="Cupcake"
                                        image={UiTexturesManager.instance.gameModeCupcake}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Skull"
                                        id="Survival"
                                        image={UiTexturesManager.instance.gameModeSkull}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Match"
                                        id="Normal"
                                        image={UiTexturesManager.instance.gameModeMatch}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Timed"
                                        id="Timed"
                                        image={UiTexturesManager.instance.gameModeTimed}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Mushroom"
                                        id="Fungus"
                                        image={UiTexturesManager.instance.gameModeMushroom}
                                    ></CarouselItem>
                                </Carousel>
                            </Container>
                            <Header
                                title={title}
                                color={colorSwatch.SubTitleHighBackground}
                                center={true}
                            />
                            <Container>
                                {isAutoPop && (
                                    <RotatableImage
                                        z={0.01}
                                        position={PositionType.Absolute}
                                        top={-50}
                                        left={-10}
                                        rounding={0}
                                        width={150}
                                        height={75}
                                        rotation={15}
                                        src={UiTexturesManager.instance.autoPop}
                                    ></RotatableImage>
                                )}
                            </Container>
                            <Row margin={20} alignSelf={Align.Center} gap={10}>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color="#ffffffff"
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={diffLevel >= 2 ? '#ffffffff' : '#ffffff80'}
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={diffLevel >= 3 ? '#ffffffff' : '#ffffff80'}
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={diffLevel >= 4 ? '#ffffffff' : '#ffffff80'}
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={diffLevel >= 5 ? '#ffffffff' : '#ffffff80'}
                                ></ColoredImage>
                            </Row>
                            <Container
                                marginTop={20}
                                height={75}
                                alignContent={Align.FlexStart}
                                padding={10}
                            >
                                <Text
                                    color={setAlpha(colorSwatch.DescriptionText, '90%')}
                                    material={rotbMats.regularTextMaterial}
                                    fontSize={24}
                                    textAlign="center"
                                >
                                    {insertNewlines(description, 44)}
                                </Text>
                            </Container>
                        </Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.SpaceAround}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.CancelButton}
                        fontSize={28}
                        icon={UiTexturesManager.instance.home}
                        iconWidth={38}
                        iconHeight={40}
                        onClick={() => goToMainMenu()}
                    >
                        {Localization.instance.get('Home')}
                    </MenuButton>
                    {isImplemented && !isLocked ? (
                        <MenuButton
                            width={250}
                            height={54}
                            margin={6}
                            color={colorSwatch.AcceptButton}
                            fontSize={32}
                            iconWidth={38}
                            iconHeight={40}
                            onClick={handleClick}
                            icon={UiTexturesManager.instance.arrow}
                        >
                            {Localization.instance.get('Next')}
                        </MenuButton>
                    ) : (
                        <Text
                            width={250}
                            height={54}
                            top={18}
                            margin={6}
                            fontSize={24}
                            textAlign="center"
                        >
                            {isImplemented ? 'Locked' : 'Coming Soon'}
                        </Text>
                    )}
                </Row>
            </Column>
        </Container>
    );
};
